import React, { useState, useEffect, useRef, useCallback } from "react";
import "./BalloonShooter.css";

const BalloonShooter = ({ onBack, isPaused }) => {
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [targetScore] = useState(50);

    // Game constants (Logical units) - Widescreen optimization
    const GAME_W = 1600;
    const GAME_H = 900;
    const ARCHER_POS = { x: 180, y: 720 };

    // Game State Ref (High performance loop)
    const game = useRef({
        balloons: [],
        arrows: [],
        aim: { angle: 0, power: 0, active: false, startX: 0, startY: 0 },
        lastSpawn: 0,
        lastFrame: 0,
        particles: []
    });

    const canvasRef = useRef(null);
    const containerRef = useRef(null);

    const spawnBalloon = useCallback(() => {
        const id = Math.random();
        const x = 400 + Math.random() * (GAME_W - 550);
        const speed = 1.0 + Math.random() * 2.2;
        const colors = ["#ff4b2b", "#38bdf8", "#a855f7", "#fbbf24", "#2ecc40", "#ec4899"];
        const color = colors[Math.floor(Math.random() * colors.length)];
        return { id, x, y: GAME_H + 60, speed, color, popped: false, phase: Math.random() * Math.PI * 2 };
    }, [GAME_W, GAME_H]);

    const spawnParticles = (x, y, color) => {
        for (let i = 0; i < 12; i++) {
            game.current.particles.push({
                x, y,
                vx: (Math.random() - 0.5) * 15,
                vy: (Math.random() - 0.5) * 15,
                life: 1.0,
                color,
                size: 2 + Math.random() * 4
            });
        }
    };

    const update = useCallback((time) => {
        if (gameOver) return;
        
        if (isPaused) {
            game.current.lastFrame = time;
            requestAnimationFrame(update);
            return;
        }

        const dt = Math.min((time - game.current.lastFrame) / 16.67, 3);
        game.current.lastFrame = time;

        // Spawn faster
        if (time - game.current.lastSpawn > 700 && game.current.balloons.length < 12) {
            game.current.balloons.push(spawnBalloon());
            game.current.lastSpawn = time;
        }

        // Update Balloons
        game.current.balloons.forEach(b => {
            b.y -= b.speed * dt;
            b.x += Math.sin(time / 600 + b.phase) * 0.8 * dt;
        });
        game.current.balloons = game.current.balloons.filter(b => b.y > -100);

        // Update Arrows
        game.current.arrows.forEach(a => {
            a.x += a.vx * dt;
            a.y += a.vy * dt;
            a.vy += 0.07 * dt; // Lower gravity for better feel

            // Collision Check
            game.current.balloons.forEach(b => {
                if (b.popped) return;
                const dx = a.x - b.x;
                const dy = a.y - b.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < 40) { // Increased hit area
                    b.popped = true;
                    spawnParticles(b.x, b.y, b.color);
                    setScore(s => {
                        const newScore = s + 1;
                        if (newScore >= targetScore) {
                            setGameOver(true);
                        }
                        return newScore;
                    });
                }
            });
        });
        
        game.current.balloons = game.current.balloons.filter(b => !b.popped);
        game.current.arrows = game.current.arrows.filter(a => a.x < GAME_W + 100 && a.y < GAME_H + 100 && a.x > -100 && a.y > -100);

        // Update Particles
        game.current.particles.forEach(p => {
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            p.vy += 0.3 * dt;
            p.life -= 0.018 * dt;
        });
        game.current.particles = game.current.particles.filter(p => p.life > 0);

        // Render to Canvas
        const ctx = canvasRef.current?.getContext("2d");
        if (ctx) {
            // Draw Sky Gradient
            const skyGrad = ctx.createLinearGradient(0, 0, 0, GAME_H);
            skyGrad.addColorStop(0, "#0c4a6e");
            skyGrad.addColorStop(0.5, "#0f172a");
            skyGrad.addColorStop(1, "#1e1b4b");
            ctx.fillStyle = skyGrad;
            ctx.fillRect(0, 0, GAME_W, GAME_H);

            // Draw Clouds
            ctx.fillStyle = "rgba(255,255,255,0.04)";
            for(let i=0; i<6; i++) {
                const cx = (100 + i*400 + time/35) % (GAME_W + 200) - 100;
                ctx.beginPath();
                ctx.arc(cx, 180 + Math.sin(time/2500 + i)*40, 60, 0, Math.PI*2);
                ctx.arc(cx + 50, 160 + Math.sin(time/2500 + i)*40, 85, 0, Math.PI*2);
                ctx.arc(cx + 110, 180 + Math.sin(time/2500 + i)*40, 60, 0, Math.PI*2);
                ctx.fill();
            }

            // Draw Balloons
            game.current.balloons.forEach(b => {
                ctx.save();
                ctx.translate(b.x, b.y);
                // String
                ctx.beginPath();
                ctx.strokeStyle = "rgba(255,255,255,0.25)";
                ctx.lineWidth = 1.5;
                ctx.moveTo(0, 35);
                ctx.bezierCurveTo(5, 50, -5, 65, 0, 85);
                ctx.stroke();
                // Body
                const grad = ctx.createRadialGradient(-10, -12, 4, 0, 0, 45);
                grad.addColorStop(0, "#fff");
                grad.addColorStop(0.2, b.color);
                grad.addColorStop(1, b.color);
                ctx.fillStyle = grad;
                ctx.beginPath();
                ctx.ellipse(0, 0, 32, 42, 0, 0, Math.PI * 2);
                ctx.fill();
                // Gloss
                ctx.fillStyle = "rgba(255,255,255,0.15)";
                ctx.beginPath();
                ctx.ellipse(-12, -15, 10, 18, -Math.PI/4, 0, Math.PI*2);
                ctx.fill();
                ctx.restore();
            });

            // Draw Particles
            game.current.particles.forEach(p => {
                ctx.globalAlpha = p.life;
                ctx.fillStyle = p.color;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size * 1.2, 0, Math.PI * 2);
                ctx.fill();
            });
            ctx.globalAlpha = 1.0;


            // Draw Archer (Proper Boy Character)
            const { angle, power, active } = game.current.aim;
            ctx.save();
            ctx.translate(ARCHER_POS.x, ARCHER_POS.y);
            
            // Shadow
            ctx.fillStyle = "rgba(0,0,0,0.25)";
            ctx.beginPath();
            ctx.ellipse(0, 85, 35, 8, 0, 0, Math.PI * 2);
            ctx.fill();

            // T-Shirt (Vibrant Yellow)
            ctx.fillStyle = "#fbbf24";
            ctx.beginPath();
            ctx.roundRect(-20, -35, 40, 70, 10);
            ctx.fill();
            // T-shirt Detail
            ctx.strokeStyle = "rgba(0,0,0,0.1)";
            ctx.lineWidth = 2;
            ctx.strokeRect(-20, -35, 40, 70);
            
            // Jeans (Denim Blue)
            ctx.fillStyle = "#3b82f6";
            ctx.fillRect(-20, 35, 17, 45); // Left Leg
            ctx.fillRect(3, 35, 17, 45);  // Right Leg
            
            // Sneakers (Red/White)
            ctx.fillStyle = "#ef4444";
            ctx.beginPath();
            ctx.roundRect(-24, 80, 24, 12, 5);
            ctx.roundRect(0, 80, 24, 12, 5);
            ctx.fill();
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(-24, 88, 24, 4);
            ctx.fillRect(0, 88, 24, 4);

            // Head (Friendly Boy)
            ctx.fillStyle = "#fee2e2";
            ctx.beginPath(); ctx.arc(0, -65, 26, 0, Math.PI * 2); ctx.fill();
            
            // Messy Hair (Brown)
            ctx.fillStyle = "#451a03";
            ctx.beginPath();
            ctx.arc(0, -75, 28, Math.PI * 0.8, Math.PI * 2.2);
            ctx.fill();
            // Hair Spikes
            for(let i=0; i<5; i++) {
                ctx.beginPath();
                ctx.moveTo(-25 + i*10, -85);
                ctx.lineTo(-20 + i*10, -100);
                ctx.lineTo(-15 + i*10, -85);
                ctx.fill();
            }

            // Eyes & Expression
            ctx.fillStyle = "#0f172a";
            ctx.beginPath(); ctx.arc(-10, -68, 3, 0, Math.PI * 2); ctx.fill();
            ctx.beginPath(); ctx.arc(10, -68, 3, 0, Math.PI * 2); ctx.fill();
            // Smile
            ctx.strokeStyle = "#0f172a";
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.arc(0, -58, 8, 0.2, Math.PI - 0.2);
            ctx.stroke();

            // Arms (Drawing based on aim)
            ctx.strokeStyle = "#fee2e2";
            ctx.lineWidth = 10;
            ctx.lineCap = "round";
            // Front Arm
            ctx.beginPath();
            ctx.moveTo(0, -15);
            ctx.lineTo(20, 5);
            ctx.stroke();
            
            // Bow System
            ctx.save();
            const drawAngle = active ? angle : -Math.PI / 4;
            ctx.rotate(drawAngle);
            
            // Bow
            ctx.strokeStyle = "#4ade80"; // Cool Green Bow
            ctx.lineWidth = 7;
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.arc(45, 0, 70, -Math.PI/2, Math.PI/2);
            ctx.stroke();

            // Bow Handle
            ctx.fillStyle = "#166534";
            ctx.fillRect(40, -12, 10, 24);
            
            // String
            ctx.strokeStyle = "rgba(255,255,255,0.7)";
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(45, -70);
            const stringPull = active ? -power/3 + 45 : 45;
            ctx.lineTo(stringPull, 0);
            ctx.lineTo(45, 70);
            ctx.stroke();

            // Trajectory
            if (active && power > 20) {
                ctx.setLineDash([8, 12]);
                ctx.strokeStyle = "rgba(255,255,255,0.3)";
                ctx.beginPath();
                ctx.moveTo(55, 0);
                ctx.lineTo(power * 6, 0);
                ctx.stroke();
                ctx.setLineDash([]);
            }

            // Aiming Arrow
            if (active) {
                ctx.save();
                ctx.translate(stringPull, 0);
                ctx.fillStyle = "#f8fafc";
                ctx.fillRect(0, -4, 75, 8);
                ctx.beginPath();
                ctx.moveTo(75, -12); ctx.lineTo(95, 0); ctx.lineTo(75, 12);
                ctx.fill();
                ctx.restore();
            }
            ctx.restore();
            ctx.restore();

            // Flying Arrows
            game.current.arrows.forEach(a => {
                ctx.save();
                ctx.translate(a.x, a.y);
                ctx.rotate(Math.atan2(a.vy, a.vx));
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(-35, -4, 70, 8);
                ctx.beginPath();
                ctx.moveTo(35, -12); ctx.lineTo(55, 0); ctx.lineTo(35, 12);
                ctx.fill();
                ctx.restore();
            });
        }

        requestAnimationFrame(update);
    }, [gameOver, spawnBalloon, targetScore, isPaused]);

    useEffect(() => {
        game.current.lastFrame = performance.now();
        const anim = requestAnimationFrame(update);
        return () => cancelAnimationFrame(anim);
    }, [update]);

    const handleStart = (e) => {
        if (gameOver) return;
        if (e.cancelable) e.preventDefault();
        
        const rect = canvasRef.current.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        const scaleX = GAME_W / rect.width;
        const scaleY = GAME_H / rect.height;
        
        game.current.aim.startX = x * scaleX;
        game.current.aim.startY = y * scaleY;
        
        setIsDragging(true);
        game.current.aim.active = true;
        game.current.aim.angle = -Math.PI / 4;
        game.current.aim.power = 0;
    };

    const handleMove = (e) => {
        if (!isDragging) return;
        if (e.cancelable) e.preventDefault();

        const rect = canvasRef.current.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        const scaleX = GAME_W / rect.width;
        const scaleY = GAME_H / rect.height;
        
        const lx = x * scaleX;
        const ly = y * scaleY;

        // Slingshot logic relative to start point
        const dx = game.current.aim.startX - lx;
        const dy = game.current.aim.startY - ly;

        game.current.aim.angle = Math.atan2(dy, dx);
        game.current.aim.power = Math.min(Math.sqrt(dx * dx + dy * dy), 200);
    };

    const handleEnd = (e) => {
        if (!isDragging) return;
        if (e && e.cancelable) e.preventDefault();
        
        setIsDragging(false);
        game.current.aim.active = false;

        const { angle, power } = game.current.aim;
        if (power > 25) {
            const speed = power / 6; // Faster arrows
            const vx = Math.cos(angle) * speed;
            const vy = Math.sin(angle) * speed;
            game.current.arrows.push({ x: ARCHER_POS.x, y: ARCHER_POS.y, vx, vy });
        }
    };

    const resetGame = () => {
        setScore(0);
        setGameOver(false);
        setIsDragging(false);
        game.current.balloons = [];
        game.current.arrows = [];
        game.current.particles = [];
        game.current.aim = { angle: 0, power: 0, active: false, startX: 0, startY: 0 };
        game.current.lastSpawn = performance.now();
        game.current.lastFrame = performance.now();
    };

    return (
        <div className="v_game-container" ref={containerRef}>
            <div className="v_game-main">
                <div className="v_canvas-wrapper">
                    <canvas
                        ref={canvasRef}
                        width={GAME_W}
                        height={GAME_H}
                        onMouseDown={handleStart}
                        onMouseMove={handleMove}
                        onMouseUp={handleEnd}
                        onMouseLeave={handleEnd}
                        onTouchStart={handleStart}
                        onTouchMove={handleMove}
                        onTouchEnd={handleEnd}
                        style={{ touchAction: 'none' }}
                    />

                    {gameOver && (
                        <div className="v_overlay">
                            <div className="v_modal">
                                <h2>Professional Victory!</h2>
                                <p>You have exceeded your quarterly balloon popping targets.</p>
                                <div className="v_final-score">
                                    <label>TOTAL POPPED</label>
                                    <span>{score}</span>
                                </div>
                                <button onClick={resetGame} className="v_restart-btn">New Campaign</button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="v_sidebar">
                    <button onClick={onBack} className="v_back-btn">← Back to Hub</button>
                    <h1>Balloon <span>Shooter</span></h1>

                    <div className="v_stats">
                        <div className="v_stat-card">
                            <label>SCORE</label>
                            <span>{score}</span>
                        </div>
                        <div className="v_stat-card">
                            <label>TARGET</label>
                            <span>{targetScore}</span>
                        </div>
                    </div>

                    <div className="v_hint">
                        <p><strong>Touch anywhere</strong> and <strong>pull back</strong> to aim. Release to fire!</p>
                    </div>

                    <button onClick={resetGame} className="v_restart-btn">Restart Game</button>
                </div>
            </div>
        </div>
    );
};

export default BalloonShooter;
